import type { AlertStatus, AnalysisData, RuleThresholds } from "../types";

const DATABASE = "ueba-console";
const STORE = "workspace";

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function setValue(key: string, value: unknown): Promise<void> {
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE, "readwrite");
    transaction.objectStore(STORE).put(value, key);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
}

async function getValue<T>(key: string): Promise<T | null> {
  const database = await openDatabase();
  const result = await new Promise<T | null>((resolve, reject) => {
    const request = database.transaction(STORE, "readonly").objectStore(STORE).get(key);
    request.onsuccess = () => resolve((request.result as T | undefined) ?? null);
    request.onerror = () => reject(request.error);
  });
  database.close();
  return result;
}

export const workspaceStorage = {
  save(data: AnalysisData) {
    return setValue("analysis", data);
  },
  load() {
    return getValue<AnalysisData>("analysis");
  },
  saveThresholds(value: RuleThresholds) {
    return setValue("thresholds", value);
  },
  loadThresholds() {
    return getValue<RuleThresholds>("thresholds");
  },
  saveStatuses(value: Record<string, AlertStatus>) {
    return setValue("statuses", value);
  },
  loadStatuses() {
    return getValue<Record<string, AlertStatus>>("statuses");
  },
  async identitySalt() {
    const saved = await getValue<string>("identity-salt");
    if (saved) return saved;
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    const value = [...bytes].map((item) => item.toString(16).padStart(2, "0")).join("");
    await setValue("identity-salt", value);
    return value;
  },
  clear() {
    return new Promise<void>((resolve, reject) => {
      const request = indexedDB.deleteDatabase(DATABASE);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error("Local database is still open in another tab"));
    });
  },
};
